import { defineStore } from 'pinia';
import { ref } from 'vue';
import { api, type UserPreferences } from '@/lib/api';
import { log } from '@/lib/log';

// X5 (ux-remediation U4/D8-final) — per-user player preferences store.
// Mirrors `stores/settings.ts`'s contract (data/loaded/loading, refresh(),
// update(patch) with optimistic merge + rollback-via-refresh on failure) but
// TYPED to the closed `UserPreferences` shape — no untyped bag, every
// consumer reads a real key. Every role (viewer included) can read AND write
// this store; there is no server-side admin gate on `/api/me/preferences`.
// Loaded whenever `auth.isAuthed` flips true and RESET whenever it flips
// false (App.vue's auth-lifecycle watcher) — this data is per-USER, so a
// logout→login user switch in one tab must never serve (or write back) the
// previous user's values. X5 review findings #1/#2.

// Pre-load placeholder — overwritten by the first `refresh()`. Mirrors the
// server's own `getDefaultPreferences()` (server/db.js) so a component that
// reads the store before boot's refresh resolves sees the same values it
// would get from the server, not an arbitrary local guess.
const INITIAL: UserPreferences = {
    arrowKeysChangeChannel: true,
    overlayDuration: 5,
    defaultVolume: 80,
    rememberVolume: true,
    lastVolume: 80,
    autoPlayNextEpisode: false,
    captionsDefaultLang: null,
    subtitleFontSize: 'medium',
    subtitleBackground: 'box',
    subtitleTextColour: 'white',
    audioDefaultLang: null,
    footballFavoriteLeagueIds: [],
};

export const usePreferencesStore = defineStore('preferences', () => {
    const data = ref<UserPreferences>({ ...INITIAL });
    const loaded = ref(false);
    const loading = ref(false);
    let refreshPromise: Promise<void> | null = null;
    let confirmedFootballFavorites = [...(INITIAL.footballFavoriteLeagueIds ?? [])];
    let authGeneration = 0;
    let footballWriteQueue: Promise<void> = Promise.resolve();
    let favoriteWriteSequence = 0;

    function refresh() {
        if (refreshPromise) return refreshPromise;
        const generation = authGeneration;
        loading.value = true;
        let request!: Promise<void>;
        request = api.me.preferences.get().then((next) => {
            if (generation === authGeneration) {
                data.value = next;
                confirmedFootballFavorites = [...(next.footballFavoriteLeagueIds ?? [])];
                loaded.value = true;
                log.debug('preferences.refresh', { keys: Object.keys(data.value).length });
            }
        }).finally(() => {
            if (generation === authGeneration) loading.value = false;
            if (refreshPromise === request) refreshPromise = null;
        });
        refreshPromise = request;
        return request;
    }

    async function update(patch: Partial<UserPreferences>) {
        const generation = authGeneration;
        const previous = data.value;
        const optimistic = { ...data.value, ...patch };
        data.value = optimistic;
        try {
            const next = await api.me.preferences.update(patch);
            if (generation !== authGeneration) return;
            data.value = next;
            confirmedFootballFavorites = [...(next.footballFavoriteLeagueIds ?? [])];
            // The PUT returns the full resolved server shape — as
            // authoritative as a GET. Without this, a failed boot refresh
            // followed by a successful save left `loaded` false and every
            // loaded-gated consumer (the player's volume attribute) inert
            // for the session despite the store holding real data.
            loaded.value = true;
            log.info('preferences.update_ok', { keys: Object.keys(patch) });
        } catch (err) {
            if (generation !== authGeneration) throw err;
            await refresh().catch(() => {
                // Preserve the last server-confirmed snapshot if the recovery
                // request is unavailable too. Local optimistic values must not
                // survive a rejected write.
                data.value = previous;
            });
            log.warn('preferences.update_failed', {
                keys: Object.keys(patch),
                error: (err as Error).message,
            });
            throw err;
        }
    }

    function updateFootballFavoriteLeagueIds(ids: string[]) {
        const nextIds = [...new Set(ids)];
        const generation = authGeneration;
        const sequence = ++favoriteWriteSequence;
        data.value = { ...data.value, footballFavoriteLeagueIds: nextIds };
        footballWriteQueue = footballWriteQueue.catch(() => {}).then(async () => {
            if (generation !== authGeneration) return;
            try {
                const next = await api.me.preferences.update({ footballFavoriteLeagueIds: nextIds });
                if (generation === authGeneration) {
                    confirmedFootballFavorites = [...(next.footballFavoriteLeagueIds ?? nextIds)];
                    data.value = sequence === favoriteWriteSequence
                        ? next
                        : { ...data.value, ...next, footballFavoriteLeagueIds: data.value.footballFavoriteLeagueIds };
                    loaded.value = true;
                }
            } catch (err) {
                if (generation === authGeneration) {
                    if (sequence === favoriteWriteSequence) {
                        await refresh().catch(() => {
                            data.value = { ...data.value, footballFavoriteLeagueIds: [...confirmedFootballFavorites] };
                        });
                    }
                    log.warn('preferences.football_favourites_failed', { error: (err as Error).message });
                }
                throw err;
            }
        });
        return footballWriteQueue;
    }

    async function toggleFootballFavoriteLeagueId(id: string) {
        if (!loaded.value) await refresh();
        const current = data.value.footballFavoriteLeagueIds ?? [];
        const next = current.includes(id) ? current.filter((entry) => entry !== id) : [...current, id];
        if (next.length > 100) throw new RangeError('You can save up to 100 favourite leagues.');
        await updateFootballFavoriteLeagueIds(next);
        return next;
    }

    /**
     * Back to the pre-auth placeholder state. Called when `auth.isAuthed`
     * flips false so the next user's session starts from a clean slate —
     * never from the previous user's data.
     */
    function reset() {
        authGeneration++;
        favoriteWriteSequence++;
        footballWriteQueue = Promise.resolve();
        refreshPromise = null;
        confirmedFootballFavorites = [...(INITIAL.footballFavoriteLeagueIds ?? [])];
        data.value = { ...INITIAL };
        loaded.value = false;
        loading.value = false;
    }

    return { data, loaded, loading, refresh, update, updateFootballFavoriteLeagueIds, toggleFootballFavoriteLeagueId, reset };
});
